import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  limit as fsLimit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { callable, db } from "../lib/firebase";
import { useCan } from "../lib/auth";
import { useCollection } from "../hooks/useCollection";
import { useStatusCounts } from "../hooks/useStatusCounts";
import { ListLimitNotice } from "../components/ListLimitNotice";
import { callableErrorMessage } from "../hooks/useCallableAction";
import {
  APPROVED_ORDER_STATUS,
  PendingOrdersQueue,
} from "../components/PendingOrdersQueue";
import { useToast } from "../components/Toast";
import { Modal } from "../components/Modal";
import { StatusBadge } from "../components/StatusBadge";
import { ChannelBadge, channelOf } from "../components/ChannelBadge";
import { FastShippingBadge } from "../components/FastShippingBadge";
import { NotifiedBadge } from "../components/NotifiedBadge";
import { LoadingBlock, Spinner } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import {
  ProductionItemsPanel,
  SourceBadge,
  sourceBadgeText,
} from "../components/ProductionItemsPanel";
import { date, n, since } from "../lib/format";
import {
  ageInDays,
  ageLevel,
  productionAmount,
  productionCustomer,
  productionHeadline,
  type AgeLevel,
} from "../lib/productionFields";

/**
 * Üretim listesi — eski `production.php`nin karşılığı. Üretim rolünün ANA ekranıdır.
 *
 * KORUNAN DAVRANIŞ
 * · İki mod: parametresiz → statü kartları (adetli), `?status=` → kayıt listesi.
 * · Üç kayıt tipi aynı listede: Sipariş (yeşil), SSH (kırmızı), Stok Siparişi (mavi).
 * · Kart tıklanınca kalem özeti açılır; toplu "Statü Değiş".
 * · Kesim ve Ayak listesi bağlantıları.
 *
 * DÜZELTİLEN DAVRANIŞ
 * · Kesim/Ayak listesi bağlantıları HER MODDA çalışır. Eskiden statü seçiliyken
 *   dropdown DOM'da olmadığı için `addEventListener` JS hatası veriyordu.
 * · Statü kartları `sortOrder`a göre sıralanır (eski production.php'de sıra yoktu).
 * · Gizli checkbox + satır tıklama hilesi kaldırıldı: tablet için büyük, gerçek,
 *   klavyeyle erişilebilir checkbox var. Satıra dokunmak yanlışlıkla seçmez.
 * · `prod-completed` geçişi İSTEMCİDEN Firestore'a YAZILMAZ; `setProductionStatus`
 *   callable'ı transaction + idempotency anahtarı ile stok düşer. Eski sistem aynı
 *   kaydı iki kez tamamlayınca stoğu iki kez düşürüyordu.
 * · Yetersiz stokta callable `failed-precondition` + eksik listesi döner; liste
 *   tablo olarak gösterilir. "Yine de tamamla" YALNIZCA boss/office'e çıkar.
 * · Maliyet/kâr bu ekranda hiçbir role gösterilmez (yalnızca sipariş tutarı).
 * · Kalemler SAYFA DEĞİŞTİRMEDEN satırın altında açılır (`ProductionItemsPanel`):
 *   görsel, ürün kartı bağlantısı, adet, üretim notu / özel istek ve rozetler.
 *   Satırın gövdesine dokunmak paneli açar; detay sayfası KALDIRILMADI, panelin
 *   içinde "Detayı aç" bağlantısı vardır. Üretim ekibi kuyruğu gezerken her
 *   kayıt için ileri-geri sayfa değiştirmek zorunda kalıyordu.
 */

const CALLABLE_SET_STATUS = "setProductionStatus";

/**
 * ★ "ÜRETİMİ BEKLEYEN" SEKMESİ — ÜRETİM AŞAMASI DEĞİL, KUYRUK GÖRÜNÜMÜ.
 *
 * Bu sekme İKİ ayrı kaynağı yan yana gösterir ve ikisi de AÇIKÇA etiketlenir:
 *
 *   1. `orders` (erpStatus == "wc-processing", yani "Onaylandı") → onaylanmış
 *      ama HENÜZ ÜRETİME GÖNDERİLMEMİŞ siparişler. Üretim kaydı YOKTUR.
 *   2. `production` (status == "prod-hold") → "Üretime Gönder" ile kaydı AÇILMIŞ
 *      ama henüz Planlama'ya alınmamış işler (`prod-hold`, sözlükte
 *      `isInitial: true`; `send.ts` yeni kaydı bu statüde açar).
 *
 * ── NİÇİN İKİSİ BİRDEN GÖSTERİLİYOR ────────────────────────────────────────
 * Yalnızca (1) gösterilseydi, gönderilen iş `prod-hold`ta doğduğu için hiçbir
 * sekmede GÖRÜNMEZ olurdu — sessizce kaybolan kayıt, bu projenin baskın hata
 * sınıfı. Yalnızca (2) gösteriliyordu ve canlıda 0'dı: atölye önündeki 15 işi
 * göremiyordu. İkisi ayrı başlıklarla gösterilir; hiçbir sayı gizlenmez.
 *
 * ⚠ Bu sekmenin sipariş göstermesi, siparişin üretime girdiği ANLAMINA GELMEZ.
 * `statuses/wc-processing.opensProduction` alanı `false` KALIR: bugün true
 * yapıldığında aynı sipariş hem "Onaylandı" hem "CNC"de göründü ve 21 hatalı
 * üretim kaydı silinmek zorunda kaldı. Planlama/Optimizasyon/CNC/Üretim/
 * Paketleme sekmeleri yalnızca `production` okumaya DEVAM EDER.
 */
const HOLD_STATUS = "prod-hold";

/**
 * `useStatusCounts` dizi kimliğine değil İÇERİĞİNE bakar; yine de modül
 * seviyesinde sabit tutmak her render'da yeni dizi üretmeyi engeller.
 */
const APPROVED_STATUS_IDS = [APPROVED_ORDER_STATUS] as const;

/** Tek sayfada okunacak en fazla üretim kaydı. Aşılırsa ekran bunu SÖYLER. */
const PRODUCTION_LIST_LIMIT = 200;

/**
 * Kaynak sipariş VARLIK KONTROLÜ için üst sınır.
 *
 * Kontrol yalnızca müşteri adı BOŞ olan sipariş kayıtlarına yapılır; canlıda
 * aktif statülerde bu 10 kayıt. Üst sınır, "Tamamlanan Üretim" gibi 200 satırlık
 * listelerde tabletin 200 ek okuma yapmasını engeller.
 */
const MAX_SOURCE_CHECKS = 40;

/** Yaşlanma seviyesine göre kart kenarı — tabletten bir bakışta görünür. */
const AGE_CARD_CLASS: Record<AgeLevel, string> = {
  fresh: "",
  warn: "border-l-4 border-l-amber-400",
  stale: "border-l-4 border-l-red-600",
};

/* ══════════════════════════════════════════════════════════════════════════
 * Paylaşılan tipler ve yardımcılar (ProductionDetail ve StockOrders da kullanır)
 * ══════════════════════════════════════════════════════════════════════════ */

export interface StatusDoc {
  label?: string;
  category?: string;
  obsolete?: boolean;
  color?: string;
  sortOrder?: number;
  /** Eski şema adı — veri hangisini taşıyorsa o kullanılır. */
  ordering?: number;
  /**
   * Bu statüden geçilebilecek statüler. Sunucu (`production/status.ts`) bu listeyi
   * zorluyor; ekran da aynı listeyi okumazsa kullanıcı reddedileceği kesin olan
   * bir hedefi seçebiliyor ve hatayı ancak işlemden SONRA görüyor.
   * ⚠ Firestore'daki alan adı `allowedTransitions`; `lib/types.ts` içindeki
   * `allowedNext` karşılığı YAZILMIYOR, o yüzden buradan okunmaz.
   */
  allowedTransitions?: string[];
}

export function statusOrder(s: StatusDoc): number {
  return s.sortOrder ?? s.ordering ?? 10000;
}

export interface ProductionItemSummary {
  title?: string;
  productName?: string;
  variantTitle?: string;
  quantity?: number;
  imageUrl?: string;
  /** Ürün kartına bağlantı için — özet bu alanı taşıyorsa kullanılır. */
  productId?: string;
}

export interface ProductionDoc {
  status?: string;
  sourceType?: "order" | "ssh" | "stockOrder";
  sourceId?: string;
  /** Satış kanalı — üretim kaydına siparişten kopyalanır. */
  channel?: string | null;
  sourceLabel?: string;
  orderName?: string;
  /** Üretim dokümanı PII taşımaz; yalnızca kısa ad (Security Rules §6). */
  customerShortName?: string;
  customerName?: string;
  priority?: string;
  note?: string;
  createdAt?: { toDate: () => Date } | null;
  /** Göç betiğinden gelen eski alan adı. */
  intime?: { toDate: () => Date } | null;
  completedAt?: { toDate: () => Date } | null;
  orderTotal?: number;
  itemSummary?: ProductionItemSummary[];
  cuttingLineCount?: number;
  stockReductionDone?: boolean;
  /** Kaynak siparişte stoktan çıkan kalem var — 1 iş gününde gönderilir. */
  fastShipping?: boolean;
  /**
   * Müşteriye HANGİ aşamalarda mail gittiği (aşama kimliği → zaman damgası).
   * `klaviyo/productionStageTrigger.ts` yazar.
   *
   * Atölye "müşteri bunu biliyor mu?" sorusunu ekrandan yanıtlasın diye
   * gösteriliyor: telefon eden müşteriye ne söylendiğini bilmeden cevap vermek
   * çelişkili iki bilgi demekti.
   */
  notifiedStages?: Record<string, unknown>;
}

/** Yetersiz stok satırı — callable `details.shortages` içinde gelir. */
export interface Shortage {
  componentId: string;
  name: string;
  unit: string;
  needed: number;
  available: number;
  missing: number;
}

export interface ProductionStatusResult {
  results?: Array<{
    productionId: string;
    ok: boolean;
    skipped?: boolean;
    forced?: boolean;
    alreadyProcessed?: boolean;
    error?: string;
    details?: { shortages?: Shortage[] } | null;
    shortages?: Shortage[];
  }>;
}

/** Hata nesnesinden veya sonuç satırından eksik bileşen listesini çıkarır. */
export function extractShortages(source: unknown): Shortage[] {
  if (!source || typeof source !== "object") return [];
  const o = source as { details?: unknown; shortages?: unknown };
  const direct = Array.isArray(o.shortages) ? o.shortages : null;
  const nested = (o.details as { shortages?: unknown } | undefined)?.shortages;
  const list = direct ?? (Array.isArray(nested) ? nested : null);
  return list ? (list as Shortage[]) : [];
}

/**
 * Callable çağrısı — `useCallableAction` yerine.
 *
 * NEDEN: `useCallableAction` hatayı bilerek tek bir Türkçe MESAJA indirger.
 * Yetersiz stok akışında ise `HttpsError.details.shortages` dizisi gerekir
 * (eksik bileşenler tablo hâlinde gösterilecek). Bu yüzden burada `callable()`
 * doğrudan çağrılır ve hata nesnesi korunur. Mesaj yine sunucudan Türkçe gelir.
 */
export function useDetailedCallable<TIn, TOut>(name: string) {
  const [pending, setPending] = useState(false);
  const fnRef = useRef<((d: TIn) => Promise<TOut>) | null>(null);
  if (fnRef.current === null) fnRef.current = callable<TIn, TOut>(name);

  const run = useCallback(
    async (
      data: TIn,
    ): Promise<
      { ok: true; out: TOut } | { ok: false; message: string; shortages: Shortage[] }
    > => {
      setPending(true);
      try {
        const out = await fnRef.current!(data);
        return { ok: true, out };
      } catch (e) {
        console.error(`Callable hatası: ${name}`, e);
        return { ok: false, message: callableErrorMessage(e), shortages: extractShortages(e) };
      } finally {
        setPending(false);
      }
    },
    [name],
  );

  return { run, pending };
}

/* ══════════════════════════════════════════════════════════════════════════
 * Yetersiz stok penceresi
 * ══════════════════════════════════════════════════════════════════════════ */

export interface ShortageState {
  message: string;
  shortages: Shortage[];
  /** "Yine de tamamla" basılınca yeniden denenecek kayıtlar. */
  retryIds: string[];
}

export function ShortageDialog({
  state,
  canForce,
  busy,
  onClose,
  onForce,
}: {
  state: ShortageState | null;
  /** boss + office. `production` rolüne bu buton GÖSTERİLMEZ. */
  canForce: boolean;
  busy: boolean;
  onClose: () => void;
  onForce: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (state) setReason("");
  }, [state]);

  return (
    <Modal
      open={state !== null}
      title="Stok yetersiz — üretim tamamlanmadı"
      description="Hiçbir stok hareketi yazılmadı. Eski sistem eksik malzemeyi sessizce atlar, stoğu şişirirdi."
      onClose={onClose}
      size="lg"
    >
      <p className="text-sm text-gray-900">{state?.message}</p>

      <div className="mt-4 overflow-x-auto">
        <table className="table-dense">
          <caption className="sr-only">Eksik bileşenler</caption>
          <thead>
            <tr>
              <th scope="col">Bileşen</th>
              <th scope="col">Gereken</th>
              <th scope="col">Mevcut</th>
              <th scope="col">Eksik</th>
            </tr>
          </thead>
          <tbody>
            {(state?.shortages ?? []).map((s) => (
              <tr key={s.componentId}>
                <td>{s.name}</td>
                <td className="tabular-nums">{n(s.needed, s.unit ? ` ${s.unit}` : "")}</td>
                <td className="tabular-nums">{n(s.available)}</td>
                <td className="tabular-nums font-semibold text-red-700">{n(s.missing)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canForce ? (
        <div className="mt-5 rounded-md border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            Yine de tamamla — stok negatife düşecek
          </p>
          <p className="mt-1 text-sm text-amber-900">
            Üretim fiilen yapıldıysa malzeme fiilen tüketilmiştir. Hareketler <em>zorlanmış</em>
            olarak işaretlenir, uyarı kaydı üretilir ve gerekçe kaydedilir.
          </p>
          <label htmlFor="force-reason" className="label mt-3">
            Gerekçe (zorunlu)
          </label>
          <textarea
            id="force-reason"
            className="input mt-1"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Örn. Sayım farkı; malzeme atölyede fiziken mevcut."
          />
          <button
            type="button"
            className="btn-danger mt-3"
            disabled={busy || reason.trim().length < 3}
            onClick={() => onForce(reason.trim())}
          >
            {busy ? "Tamamlanıyor…" : "Yine de tamamla"}
          </button>
        </div>
      ) : (
        <p className="mt-5 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
          Eksik malzemenin stok girişi yapıldıktan sonra üretimi tekrar tamamlayabilirsiniz.
          Negatif stokla tamamlama yetkisi ofis ve iş sahibindedir.
        </p>
      )}
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * Statü kartları
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Adetli statü kartları — Üretim, Kesim Listesi ve Ayak Listesi ekranlarının
 * ORTAK statü seçicisi.
 *
 * ★ NİÇİN PAYLAŞILDI: kart ızgarası üç ekranda üç kez kopyalanmıştı ve adet
 * yalnızca Üretim ekranında vardı. Kesim/Ayak ekranında atölye, hangi statüde
 * kaç iş olduğunu görmeden kart seçiyor, çoğu zaman BOŞ listeye düşüyordu.
 *
 * `onlyOrders` — kesim ve ayak listesi yalnızca SİPARİŞTEN doğan üretimleri
 * işler; adet de aynı süzgeçle sayılmalı, yoksa kart "14 kayıt" der, liste 5
 * satır açar. Sorgu `firestore.indexes.json` içindeki
 * (`sourceType`, `status`, `createdAt`) bileşik indeksinin eşitlik önekini
 * kullanır — ek indeks gerektirmez.
 */
/**
 * Bir statü kartına EKLENEN, `production` DIŞI bir kaynaktan gelen adet.
 *
 * Bugün tek kullanıcısı "Üretimi Bekleyen" kartıdır: oradaki asıl sayı
 * `orders` koleksiyonundan gelir (onaylanmış, üretime gönderilmemiş sipariş).
 * Üretim kaydı adedi GİZLENMEZ, yanına yazılır — kart ile listenin aynı şeyi
 * söylemesi şart. ("172 kayıt" diyen kartın altında 7 satır çıkması bu projenin
 * bilinen hatasıydı; sayaç dokümanları o yüzden silindi.)
 */
export interface ExtraCardCount {
  /** `undefined` = hâlâ sayılıyor, `null` = SAYILAMADI (sıfır DEĞİL). */
  count: number | null | undefined;
  /** Adedin birimi, ör. "onaylı sipariş". */
  noun: string;
  /** Kartın altına basılan tek satırlık açıklama. */
  hint?: string;
}

export function StatusCountCards({
  statuses,
  hrefFor,
  onlyOrders = false,
  extraCounts,
}: {
  statuses: Array<StatusDoc & { id: string }>;
  hrefFor: (statusId: string) => string;
  onlyOrders?: boolean;
  extraCounts?: Record<string, ExtraCardCount>;
}) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [countError, setCountError] = useState<string | null>(null);
  const ids = statuses.map((s) => s.id).join(",");

  useEffect(() => {
    let cancelled = false;
    const list = ids ? ids.split(",") : [];
    if (list.length === 0) return;

    // Adet için toplama sorgusu: doküman indirilmez. Eski sistem tüm satırları
    // çekip PHP'de GROUP BY yapıyordu.
    Promise.all(
      list.map(async (tech) => {
        const snap = await getCountFromServer(
          onlyOrders
            ? query(
                collection(db, "production"),
                where("status", "==", tech),
                where("sourceType", "==", "order"),
              )
            : query(collection(db, "production"), where("status", "==", tech)),
        );
        return [tech, snap.data().count] as const;
      }),
    )
      .then((pairs) => {
        if (!cancelled) setCounts(Object.fromEntries(pairs));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        console.error("Üretim statü sayımı başarısız", e);
        setCountError("Statü adetleri okunamadı. Listeler yine de açılabilir.");
      });

    return () => {
      cancelled = true;
    };
  }, [ids, onlyOrders]);

  return (
    <div className="space-y-3">
      {countError ? (
        <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {countError}
        </p>
      ) : null}

      <ul role="list" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {statuses.map((s) => {
          const prod = counts[s.id];
          const extra = extraCounts?.[s.id];

          /* Üretim kaydı adedi — her iki durumda da aynı biçimde yazılır.
             `undefined` "sayılıyor", sıfır ise AÇIKÇA "0" basılır: bu kartta
             sıfırı görmek "üretime hiçbir şey gönderilmemiş" bilgisidir. */
          const prodText =
            prod === undefined
              ? "üretim kaydı sayılıyor…"
              : `${prod} üretim kaydı${onlyOrders ? " (siparişten)" : ""}`;

          const extraText = extra
            ? extra.count === undefined
              ? "sayılıyor…"
              : extra.count === null
                ? `— ${extra.noun} (sayılamadı)`
                : `${extra.count} ${extra.noun}`
            : null;

          return (
            <li key={s.id}>
              <Link
                to={hrefFor(s.id)}
                className={`group flex min-h-[88px] flex-col justify-between gap-1.5 rounded-xl bg-white p-4 shadow-card ring-1 ring-gray-200/80 transition hover:-translate-y-0.5 hover:shadow-md hover:ring-primary-300 ${
                  prod === 0 && !(extra?.count) ? "opacity-60 hover:opacity-100" : ""
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700 group-hover:text-gray-950">
                    <span
                      aria-hidden="true"
                      className="h-2 w-2 shrink-0 rounded-full bg-gray-300"
                      style={s.color && s.color.startsWith("#") ? { backgroundColor: s.color } : undefined}
                    />
                    {s.label ?? s.id}
                  </span>
                  <span className="text-2xl font-bold tabular-nums tracking-tight text-gray-950">
                    {extra && typeof extra.count === "number" ? extra.count : prod ?? ""}
                  </span>
                </span>
                <span className="text-xs text-gray-500">
                  {extraText ? `${extraText} · ${prodText}` : prodText}
                </span>
                {/* ★ Kartın ne anlattığı EKRANDA yazar — "Üretimi Bekleyen"
                    kuyruğu ile gerçek üretim aşamaları karıştırıldığı için
                    bugün 21 hatalı üretim kaydı oluşmuştu. */}
                {extra?.hint ? (
                  <span className="text-xs text-gray-500">{extra.hint}</span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * Ekran
 * ══════════════════════════════════════════════════════════════════════════ */

export function Production() {
  const [params] = useSearchParams();
  const status = params.get("status") ?? "";
  const { isStaff } = useCan();
  const toast = useToast();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [targetStatus, setTargetStatus] = useState("");
  const [shortage, setShortage] = useState<ShortageState | null>(null);

  const setStatus = useDetailedCallable<
    { productionIds: string[]; status: string; allowNegative?: boolean; reason?: string },
    ProductionStatusResult
  >(CALLABLE_SET_STATUS);

  /**
   * Statü sözlüğü. Tek `where`; `obsolete` filtresi ve sıralama istemcide.
   * NEDEN: sıralama alanı veride `sortOrder` mı `ordering` mi belirsiz;
   * `orderBy` kullanılsaydı alanı olmayan statü sessizce kaybolurdu.
   */
  const statusesQ = useCollection<StatusDoc>(
    () => query(collection(db, "statuses"), where("category", "==", "production")),
    [],
  );

  const statuses = useMemo(
    () =>
      statusesQ.docs
        .filter((s) => s.obsolete !== true)
        .sort((a, b) => statusOrder(a) - statusOrder(b)),
    [statusesQ.docs],
  );

  const current = statuses.find((s) => s.id === status);

  /**
   * "Üretimi Bekleyen" kartındaki ASIL sayı: onaylanmış ama üretime
   * gönderilmemiş sipariş adedi. `getCountFromServer` ile CANLI sayılır
   * (`useStatusCounts`) — donmuş `counters/*` dokümanı KULLANILMAZ.
   *
   * Süzgeç yalnızca `erpStatus`tır; `productionId == ""` DEĞİL. Canlıda onaylı
   * 15 siparişin 4'ünde `productionId`/`productionStatus` alanları bugün silinen
   * üretim kayıtlarından kalma ölü işaretçilerdi ve o süzgeç bu 4 gerçek işi
   * sessizce eleyecekti. Bkz. `components/PendingOrdersQueue.tsx`.
   */
  const approved = useStatusCounts("orders", "erpStatus", APPROVED_STATUS_IDS);

  /**
   * Açık statüdeki GERÇEK kayıt sayısı — liste `limit(200)` ile okunduğu için
   * kart ile liste ayrışabiliyor. Sayı, "kaç kayıt görünmüyor" cümlesini
   * kurabilmek için gerekli (bkz. `ListLimitNotice`).
   */
  const currentCount = useStatusCounts("production", "status", status ? [status] : []);

  /**
   * ★ Yalnızca BURADAN GEÇİLEBİLECEK statüler listelenir.
   *
   * Ekran tek statüyü gösterdiği için seçili kayıtların hepsi aynı statüdedir;
   * hedef kümesi kesin olarak `current.allowedTransitions`tır. Liste süzülmediği
   * için üretim ekibi "Üretimi Bekleyen" kayıtları doğrudan "Tamamlanan Üretim"e
   * taşımayı deneyip toplu hata alıyordu — akış zaten önce Üretim'den geçmeyi
   * şart koşuyor. Boş liste = kısıt yok (sunucu da öyle davranıyor).
   */
  const reachableStatuses = useMemo(() => {
    const allowed = current?.allowedTransitions ?? [];
    if (allowed.length === 0) return statuses;
    const set = new Set(allowed);
    return statuses.filter((s) => set.has(s.id));
  }, [statuses, current]);

  /**
   * Kayıt listesi. Üst sınır eski `LIMIT 200` kuralının yerini alır: tamamlanmış
   * üretim binlerce kayda ulaşır ve tabletin belleğini tüketir.
   */
  const listQ = useCollection<ProductionDoc>(
    () =>
      status
        ? query(
            collection(db, "production"),
            where("status", "==", status),
            orderBy("createdAt", "desc"),
            fsLimit(PRODUCTION_LIST_LIMIT),
          )
        : null,
    [status],
  );

  useEffect(() => {
    setSelected(new Set());
    setExpanded(new Set());
  }, [status]);

  /* ── Kaynağı KAYIP kayıtlar ───────────────────────────────────────────────
   *
   * Canlı ölçüm (2026-08-14): `sourceType: "order"` olan 52 üretim kaydının
   * kaynak siparişi `orders` koleksiyonunda YOK (göç kaybı). Bunların 10'u hâlâ
   * aktif statüde ("Üretim"de 9, "Planlama"da 1) ve 7'si 7–10 gündür bekliyor.
   * Bu kayıtlarda `orderName`, `customerName`, `customerShortName`, `orderTotal`
   * alanlarının HEPSİ boş; ekran onları sessizce "Sipariş 77554" diye basıyor,
   * atölye adı olmayan sıradan bir iş sanıyordu.
   *
   * Alan boşluğuna BAKARAK karar VERİLEMEZ: adı boş 241 kaydın yalnızca 52'sinde
   * kaynak gerçekten kayıp. O yüzden varlık kontrolü yapılır — ama sadece adı
   * boş satırlar için ve `MAX_SOURCE_CHECKS` üst sınırıyla. Sonuç önbelleklenir;
   * aynı sipariş ikinci kez okunmaz.
   */
  const [sourceExists, setSourceExists] = useState<Record<string, boolean>>({});
  const checkedRef = useRef<Set<string>>(new Set());

  const namelessKey = useMemo(
    () =>
      [
        ...new Set(
          listQ.docs
            .filter(
              (p) =>
                p.sourceType === "order" &&
                String(p.sourceId ?? "") !== "" &&
                productionCustomer(p) === "",
            )
            .map((p) => String(p.sourceId)),
        ),
      ]
        .slice(0, MAX_SOURCE_CHECKS)
        .sort()
        .join(","),
    [listQ.docs],
  );

  useEffect(() => {
    let cancelled = false;
    const ids = (namelessKey ? namelessKey.split(",") : []).filter(
      (id) => !checkedRef.current.has(id),
    );
    if (ids.length === 0) return;
    ids.forEach((id) => checkedRef.current.add(id));

    void Promise.all(
      ids.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, "orders", id));
          return [id, snap.exists()] as const;
        } catch (e) {
          // Okuma hatası "kayıp" DEĞİLDİR: yetki/ağ hatası yüzünden kayda
          // haksız yere "kaynağı yok" damgası vurmak, olmayan bir veri
          // kaybını ihbar etmek olurdu.
          console.error("Kaynak sipariş kontrol edilemedi", id, e);
          return [id, true] as const;
        }
      }),
    ).then((pairs) => {
      if (!cancelled) setSourceExists((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });

    return () => {
      cancelled = true;
    };
  }, [namelessKey]);

  /**
   * Bitiş statüsünde yaşlanma işareti BASILMAZ: "Tamamlanan Üretim"deki 2 yıllık
   * kayıt geciken iş değildir. Bitiş statüsünün ayırt edici işareti, sözlükte
   * geçiş listesinin boş olmasıdır (`prod-completed`, `prod-cancelled`).
   */
  const isTerminalStatus = (current?.allowedTransitions?.length ?? -1) === 0;

  /**
   * Seçili hedef her zaman listedeki bir seçenek olmalı. Sütun değişince eski
   * hedef geçersiz kalabiliyor; bu durumda `<select>` görsel olarak ilk seçeneğe
   * düşer ama state eski değeri taşımaya devam eder — kullanıcı gördüğünden
   * BAŞKA bir statüye taşırdı.
   */
  useEffect(() => {
    if (reachableStatuses.length === 0) return;
    if (reachableStatuses.some((s) => s.id === targetStatus)) return;
    setTargetStatus(reachableStatuses[0]!.id);
  }, [reachableStatuses, targetStatus]);

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** Toplu statü değişimi. Sonuç satır satır değerlendirilir — sessiz başarı yok. */
  const runStatusChange = async (ids: string[], allowNegative: boolean, reason?: string) => {
    if (ids.length === 0 || !targetStatus) return;

    const res = await setStatus.run({
      productionIds: ids,
      status: targetStatus,
      ...(allowNegative ? { allowNegative: true, reason } : {}),
    });

    if (!res.ok) {
      // Üst düzey hata: yetersiz stok ise eksik listesi tabloda gösterilir.
      if (res.shortages.length > 0) {
        setShortage({ message: res.message, shortages: res.shortages, retryIds: ids });
        return;
      }
      toast.error(res.message);
      return;
    }

    const results = res.out.results ?? [];
    const failed = results.filter((r) => !r.ok);
    const withShortage = failed.find((r) => extractShortages(r).length > 0);

    if (withShortage) {
      setShortage({
        message:
          withShortage.error ??
          "Üretim tamamlanamadı: bileşen stoğu yetersiz. Hiçbir stok hareketi yazılmadı.",
        shortages: extractShortages(withShortage),
        retryIds: ids,
      });
      return;
    }

    setShortage(null);

    if (failed.length > 0) {
      toast.error(
        `${results.length - failed.length} kayıt güncellendi, ${failed.length} kayıt güncellenemedi.`,
        failed.map((f) => `${f.productionId}: ${f.error ?? "bilinmeyen hata"}`).join("\n"),
      );
      setSelected(new Set());
      return;
    }

    const forced = results.filter((r) => r.forced).length;
    const skipped = results.filter((r) => r.skipped).length;
    const already = results.filter((r) => r.alreadyProcessed).length;

    toast.success(
      `${results.length - skipped} üretim kaydı güncellendi.`,
      [
        skipped > 0 ? `${skipped} kayıt zaten bu statüdeydi.` : "",
        already > 0 ? `${already} kayıtta stok daha önce düşülmüştü, tekrar düşülmedi.` : "",
        forced > 0 ? `${forced} kayıtta stok negatife düştü; gerekçe kaydedildi.` : "",
      ]
        .filter(Boolean)
        .join(" ") || undefined,
    );
    setSelected(new Set());
  };

  const busy = setStatus.pending;

  /** "Üretimi Bekleyen" sekmesinde ekran İKİ bölümlüdür (bkz. HOLD_STATUS). */
  const isHoldTab = status === HOLD_STATUS;

  /**
   * Toplu statü değiştirme çubuğu — YALNIZCA `production` kayıtlarına işler.
   *
   * Değişken olarak çıkarıldı çünkü "Üretimi Bekleyen" sekmesinde ekranın üstünü
   * sipariş kuyruğu kaplıyor. Çubuk orada dursaydı, kullanıcı sipariş satırlarını
   * seçip "Statü Değiş"e basmayı deneyecekti — oysa o siparişlerin üretim kaydı
   * HENÜZ YOK. Bu sekmede çubuk, ait olduğu üretim kaydı listesinin hemen üstüne
   * iner.
   */
  const statusChangeToolbar = status ? (
    <div className="mt-3 flex flex-wrap items-center gap-2 noprint">
      <span className="text-sm text-gray-600">
        {selected.size > 0 ? `${selected.size} üretim kaydı seçili` : "İşlem için üretim kaydı seçin"}
      </span>
      <label htmlFor="prod-target-status" className="sr-only">
        Yeni statü
      </label>
      <select
        id="prod-target-status"
        className="select w-auto"
        value={targetStatus}
        onChange={(e) => setTargetStatus(e.target.value)}
        disabled={busy}
      >
        {reachableStatuses.map((s) => (
          <option key={s.id} value={s.id}>
            {s.label ?? s.id}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn-danger"
        disabled={selected.size === 0 || !targetStatus || busy}
        onClick={() => void runStatusChange([...selected], false)}
      >
        Statü Değiş
      </button>
      {busy ? <Spinner size="sm" label="İşleniyor" /> : null}
    </div>
  ) : null;

  return (
    <div className="space-y-5">
      {/* Başlık + kırıntı */}
      <div>
        <nav aria-label="Sayfa yolu" className="text-sm">
          <ol role="list" className="flex items-center gap-2 text-gray-500">
            <li>
              <Link to="/production" className="hover:text-gray-700">
                Üretim
              </Link>
            </li>
            {current ? (
              <>
                <li aria-hidden="true">/</li>
                <li className="text-gray-700">{current.label ?? current.id}</li>
              </>
            ) : null}
          </ol>
        </nav>

        <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">
            {current ? (current.label ?? current.id) : "Üretim"}
          </h1>

          <div className="flex flex-wrap items-center gap-2 noprint">
            {/* Kesim/Ayak listesi bağlantıları HER MODDA erişilebilir. */}
            <Link
              className="btn-secondary"
              to={status ? `/cutting-list?status=${encodeURIComponent(status)}` : "/cutting-list"}
            >
              Kesim Listesi
            </Link>
            <Link
              className="btn-secondary"
              to={status ? `/leg-list?status=${encodeURIComponent(status)}` : "/leg-list"}
            >
              Ayak Listesi
            </Link>
            {status ? (
              <button type="button" className="btn-secondary" onClick={() => window.print()}>
                Yazdır
              </button>
            ) : null}
          </div>
        </div>

        {/* Kuyruk sekmesinde çubuk aşağıya, üretim kaydı listesinin üstüne iner. */}
        {isHoldTab ? null : statusChangeToolbar}
      </div>

      {statusesQ.error ? (
        <div
          role="alert"
          className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {statusesQ.error}
        </div>
      ) : null}

      {/* MOD A — statü kartları */}
      {!status ? (
        statusesQ.loading ? (
          <LoadingBlock />
        ) : statuses.length === 0 ? (
          <EmptyState
            title="Üretim statüsü tanımlı değil"
            description="statuses koleksiyonunda 'production' kategorisinde kayıt yok."
            tone="warning"
          />
        ) : (
          <StatusCountCards
            statuses={statuses}
            hrefFor={(id) => `/production?status=${encodeURIComponent(id)}`}
            extraCounts={{
              [HOLD_STATUS]: {
                count: approved.loading
                  ? undefined
                  : (approved.counts[APPROVED_ORDER_STATUS] ?? null),
                noun: "onaylı sipariş",
                hint: "Onaylanmış, henüz üretime gönderilmemiş siparişler",
              },
            }}
          />
        )
      ) : null}

      {/* MOD B-0 — SİPARİŞ KUYRUĞU (yalnızca "Üretimi Bekleyen" sekmesinde)
          Kaynağı `orders`; ÜRETİM KAYDI OLUŞTURMAZ. Aşağıdaki üretim kaydı
          listesiyle karışmasın diye kendi başlığı ve kendi araç çubuğu vardır. */}
      {isHoldTab ? <PendingOrdersQueue /> : null}

      {/* MOD B — üretim KAYITLARI listesi (`production` koleksiyonu) */}
      {isHoldTab ? (
        <div className="border-t border-gray-200 pt-5">
          <h2 className="text-lg font-semibold text-gray-900">
            Üretim kaydı açılmış, henüz Planlama'ya alınmamış işler
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Bunlar "Üretime Gönder" ile <span className="font-semibold">üretim kaydı açılmış</span>{" "}
            işlerdir (sipariş, SSH veya stok siparişi). Buradan Planlama'ya taşınırlar.
          </p>
          {statusChangeToolbar}
        </div>
      ) : null}

      {status ? (
        listQ.loading ? (
          <LoadingBlock label="Üretim kayıtları yükleniyor" />
        ) : listQ.error ? (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900"
          >
            {listQ.error}
          </div>
        ) : listQ.docs.length === 0 ? (
          <EmptyState
            title="Bu statüde üretim kaydı yok"
            description="Sipariş, SSH veya stok siparişi üretime gönderildiğinde burada görünür."
          />
        ) : (
          <ul role="list" className="space-y-3">
            {listQ.docs.map((p) => {
              const isSelected = selected.has(p.id);
              const isOpen = expanded.has(p.id);
              const created = p.createdAt ?? p.intime ?? null;
              const badgeText = sourceBadgeText(p);
              const panelId = `prod-items-${p.id}`;

              /** Kaynak sipariş veritabanında YOK mu? (yalnızca kontrol edilmiş satırlar) */
              const sourceMissing =
                p.sourceType === "order" && sourceExists[String(p.sourceId ?? "")] === false;

              /* ★ Başlık ile rozet AYNI METNİ basıyordu: müşteri adı yoksa
                 `productionHeadline` de `productionSourceTitle`a düşüyor ve
                 "Sipariş 77554" yan yana iki kez görünüyordu. Metin aynıysa
                 rozet yalnızca TÜRÜ yazar; renk ve bilgi korunur, tekrar gider. */
              const headline = productionHeadline(p) || badgeText;
              const badgeDuplicatesHeadline = headline === badgeText;

              /** Kuyrukta bekleme süresi — bitiş statüsünde işaretlenmez. */
              const days = ageInDays(created);
              const level: AgeLevel = isTerminalStatus ? "fresh" : ageLevel(days);

              const amount = productionAmount(p, { sourceMissing });

              /** Panelde de gösterilen ikincil rozetler — tek yerde üretilir. */
              const extraBadges = (
                <>
                  <StatusBadge
                    technicName={p.status}
                    label={current?.label ?? p.status}
                    color={current?.color}
                    size="sm"
                  />
                  {/* Atölyenin ilk bakışta görmesi gereken: bu kayıt 1 iş gününde çıkacak. */}
                  <FastShippingBadge fastShipping={p.fastShipping} />
                  <NotifiedBadge notifiedStages={p.notifiedStages} />
                  {p.priority === "urgent" || p.priority === "Acil" ? (
                    <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                      Acil
                    </span>
                  ) : null}
                  {/* ★ YAŞLANMA: canlıda aktif 31 kaydın 7'si 7 gün ve üzeri
                      bekliyordu ve hiçbir görsel farkı yoktu. */}
                  {level !== "fresh" && days !== null ? (
                    <span
                      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${
                        level === "stale" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-900"
                      }`}
                    >
                      {days} gündür bekliyor
                    </span>
                  ) : null}
                  {sourceMissing ? (
                    <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                      kaynak sipariş kayıp
                    </span>
                  ) : null}
                  {p.stockReductionDone ? (
                    <span className="inline-flex items-center rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                      stok düşüldü
                    </span>
                  ) : null}
                </>
              );

              return (
                <li
                  key={p.id}
                  className={`card ${AGE_CARD_CLASS[level]} ${
                    isSelected ? "ring-2 ring-primary-500" : ""
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-3 p-3 sm:p-4">
                    {/* TABLET: dokunma hedefi 44px. Checkbox'ın kendisi 24px;
                        çevresindeki etiket kutusu hedefi büyütür. */}
                    <label className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center noprint">
                      <span className="sr-only">{`${headline} kaydını seç`}</span>
                      <input
                        type="checkbox"
                        className="h-6 w-6 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={isSelected}
                        onChange={() => toggleSelect(p.id)}
                      />
                    </label>

                    {/* Satır gövdesi paneli açıp kapatır: atölye tabletinde
                        büyük dokunma alanı, sayfa değişmez. Checkbox kardeş
                        elemandır — iç içe tıklanabilir öğe YOKTUR. */}
                    <button
                      type="button"
                      className="min-w-0 flex-1 rounded-md p-1 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggleExpand(p.id)}
                    >
                      {/* ★ `??` yerine `productionHeadline`: sunucu `customerName: ""`
                          yazdığı için `??` zinciri kırılmıyor ve başlık BOŞ
                          basılıyordu. Yardımcı boş dizeyi de eksik sayar; ayrıca
                          artık TAM AD önceliklidir (atölye "Ezgi" değil
                          "Ezgi Ataş" görür). */}
                      <span className="block text-base font-semibold text-gray-900">{headline}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        {/* Kanal rozeti: Trendyol siparişlerinin kargolama
                            penceresi dar, atölye önceliği farklı. */}
                        {p.sourceType === "order" ? (
                          <ChannelBadge channel={channelOf(p)} />
                        ) : null}
                        <SourceBadge source={p} compact={badgeDuplicatesHeadline} />
                        {extraBadges}
                        <span>{since(created)}</span>
                      </span>

                      {/* ★ KAYNAĞI KAYIP KAYIT — sessizce "adı yok" diye
                          geçilmez. Atölyeye ne yapacağı da yazılır. */}
                      {sourceMissing ? (
                        <span role="alert" className="mt-2 block rounded-md bg-red-50 p-2 text-xs text-red-900">
                          <span className="font-semibold">Kaynak sipariş veritabanında yok.</span>{" "}
                          Bu kayıt eski sistemden geldi, sipariş kaydı göç sırasında kayboldu;
                          müşteri adı, kalem listesi ve tutar okunamıyor. Kesim ve ayak listesinde de
                          satır üretmez. <span className="font-semibold">Yapılacak:</span> işi
                          başlatmadan önce ofise bildirin ve sipariş bilgisini kâğıt/eski sistem
                          üzerinden teyit ettirin.
                        </span>
                      ) : null}
                    </button>

                    <div className="hidden text-right sm:block">
                      {/* Sipariş TUTARI üç role de açıktır; gizli olan maliyet ve kârdır.
                          Tutar kuralı tek yerde: `productionAmount`. */}
                      <p className="text-sm tabular-nums text-gray-900" title={amount.reason}>
                        {amount.text}
                        {amount.reason ? <span className="sr-only"> — {amount.reason}</span> : null}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">{date(created)}</p>
                    </div>

                    <button
                      type="button"
                      className="btn-ghost noprint"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggleExpand(p.id)}
                    >
                      {isOpen ? "Kalemleri gizle" : "Kalemler"}
                    </button>

                    <Link
                      className="btn-secondary noprint"
                      to={`/production/${encodeURIComponent(p.id)}`}
                    >
                      Detay
                    </Link>
                  </div>

                  {/* Panel yalnızca açıkken monte edilir; kapalı satır Firestore
                      okuması yapmaz. */}
                  {isOpen ? (
                    <div id={panelId}>
                      <ProductionItemsPanel
                        productionId={p.id}
                        source={p}
                        itemSummary={p.itemSummary}
                        note={p.note}
                        badges={extraBadges}
                        sourceMissing={sourceMissing}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )
      ) : null}

      {/* ★ SESSİZ KESME YOK. Liste sınıra dayandıysa kaç kaydın görünmediği
          yazılır; kullanıcı kartta 4.470 görüp listede 200 sayınca "veriler
          bağlı değil" sonucuna varıyordu. */}
      {status ? (
        <ListLimitNotice
          shown={listQ.docs.length}
          total={currentCount.counts[status]}
          limit={PRODUCTION_LIST_LIMIT}
          hint="Eski kayıtlar için üstteki arama kutusunu ya da sipariş numarasını kullanın."
        />
      ) : null}

      <ShortageDialog
        state={shortage}
        canForce={isStaff}
        busy={busy}
        onClose={() => setShortage(null)}
        onForce={(reason) => {
          const ids = shortage?.retryIds ?? [];
          setShortage(null);
          void runStatusChange(ids, true, reason);
        }}
      />
    </div>
  );
}

export default Production;
